/**
 * Date and time for a scheduled post.
 *
 * Replaces `<input type="datetime-local">`. The native control renders its month
 * grid as browser chrome, which the page cannot position or constrain — anchored
 * inside the composer's 300px right rail it opens against the edge of the screen
 * and is clipped there, with no CSS able to reach it. This grid is ordinary DOM
 * in a portal, so it flips instead of clipping.
 *
 * Month and weekday names come from `Intl` rather than the dictionaries: they are
 * data the platform already has in every locale, and hand-translating nineteen
 * strings into `en` and `bn` would be work that goes stale the moment a third
 * language is added.
 */

import { useMemo, useState } from 'react';
import { CalendarDays, ChevronLeft, ChevronRight } from 'lucide-react';

import { useTranslation } from '../../../context/LanguageContext';
import { Popover, PopoverContent, PopoverTrigger } from '../../core/Popover';
import { cn } from '../../../lib/utils';

interface DateTimePickerProps {
  /** UTC ISO string, or null when nothing is chosen yet. */
  value: string | null;
  onChange: (iso: string | null) => void;
}

export function DateTimePicker({ value, onChange }: Readonly<DateTimePickerProps>) {
  const { t, language } = useTranslation();
  const locale = language === 'bn' ? 'bn-BD' : 'en-US';

  const selected = value ? new Date(value) : null;
  const [open, setOpen] = useState(false);

  // The month on screen, which is not the same thing as the month selected: the
  // user pages through the calendar without committing to anything until a day
  // is clicked.
  const [viewMonth, setViewMonth] = useState(() => startOfMonth(selected ?? new Date()));

  const weekdays = useMemo(() => weekdayNames(locale), [locale]);
  const days = useMemo(() => monthGrid(viewMonth), [viewMonth]);

  const monthLabel = viewMonth.toLocaleDateString(locale, { month: 'long', year: 'numeric' });

  /**
   * Yesterday and earlier are not choices. A post scheduled into the past either
   * fires immediately or never, and both are surprises.
   */
  const today = startOfDay(new Date());

  const pickDay = (day: Date): void => {
    // Keeps the time already chosen. Picking a different day should not silently
    // reset a time the user set deliberately — and with no time yet, the next
    // whole hour is a likelier intent than midnight.
    const base = selected ?? nextWholeHour();
    const next = new Date(day);
    next.setHours(base.getHours(), base.getMinutes(), 0, 0);
    onChange(next.toISOString());
  };

  const pickTime = (hhmm: string): void => {
    if (!hhmm) return;
    const [hours, minutes] = hhmm.split(':').map(Number);
    const next = new Date(selected ?? today);
    next.setHours(hours, minutes, 0, 0);
    onChange(next.toISOString());
  };

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        // Reopening should land on the chosen date rather than wherever the user
        // last paged to and abandoned.
        if (next) setViewMonth(startOfMonth(selected ?? new Date()));
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            'flex w-full items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-left text-[13px] transition-colors',
            'hover:border-slate-300 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100',
            selected ? 'text-slate-800' : 'text-slate-400'
          )}
        >
          <CalendarDays className="h-3.5 w-3.5 shrink-0 text-slate-400" />
          <span className="min-w-0 flex-1 truncate">
            {selected
              ? selected.toLocaleString(locale, {
                  dateStyle: 'medium',
                  timeStyle: 'short',
                })
              : t('composer.schedule.pickDateTime')}
          </span>
        </button>
      </PopoverTrigger>

      {/* Anchored to the end of the trigger because the trigger lives in a
          right-hand rail: start-alignment would push the panel further toward
          the edge it is already close to. */}
      <PopoverContent align="end" className="w-[268px]">
        <div className="mb-2 flex items-center justify-between">
          <button
            type="button"
            aria-label={t('composer.schedule.prevMonth')}
            onClick={() => setViewMonth(addMonths(viewMonth, -1))}
            className="rounded-md p-1 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-800"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <span className="text-[13px] font-semibold text-slate-800">{monthLabel}</span>
          <button
            type="button"
            aria-label={t('composer.schedule.nextMonth')}
            onClick={() => setViewMonth(addMonths(viewMonth, 1))}
            className="rounded-md p-1 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-800"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>

        <div className="grid grid-cols-7 gap-0.5">
          {weekdays.map((weekday) => (
            <span
              key={weekday.key}
              className="pb-1 text-center text-[10px] font-semibold uppercase text-slate-400"
            >
              {weekday.label}
            </span>
          ))}

          {days.map(({ key, date }) => {
            // Destructured to a const so the null check narrows inside the click
            // handler too — a property read would not, and would need a cast.
            if (date === null) return <span key={key} />;

            return (
              <button
                key={key}
                type="button"
                disabled={date < today}
                onClick={() => pickDay(date)}
                className={cn(
                  'h-8 rounded-md text-[12px] transition-colors',
                  'disabled:cursor-not-allowed disabled:text-slate-300 disabled:hover:bg-transparent',
                  isSameDay(date, selected)
                    ? 'bg-blue-600 font-semibold text-white hover:bg-blue-700'
                    : 'text-slate-700 hover:bg-slate-100',
                  isSameDay(date, today) && !isSameDay(date, selected) && 'font-bold text-blue-600'
                )}
              >
                {date.toLocaleDateString(locale, { day: 'numeric' })}
              </button>
            );
          })}
        </div>

        <div className="mt-3 flex items-center gap-2 border-t border-slate-200 pt-2.5">
          <span className="text-[11px] font-semibold text-slate-600">
            {t('composer.schedule.time')}
          </span>
          <input
            type="time"
            value={selected ? `${pad(selected.getHours())}:${pad(selected.getMinutes())}` : ''}
            onChange={(e) => pickTime(e.target.value)}
            className="flex-1 rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-[12.5px] text-slate-800 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
          />
          {selected && (
            <button
              type="button"
              onClick={() => {
                onChange(null);
                setOpen(false);
              }}
              className="shrink-0 text-[11px] font-semibold text-slate-500 transition-colors hover:text-rose-600"
            >
              {t('composer.schedule.clear')}
            </button>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

const pad = (n: number): string => String(n).padStart(2, '0');

const startOfDay = (date: Date): Date =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate());

const startOfMonth = (date: Date): Date => new Date(date.getFullYear(), date.getMonth(), 1);

const addMonths = (date: Date, delta: number): Date =>
  new Date(date.getFullYear(), date.getMonth() + delta, 1);

const isSameDay = (a: Date, b: Date | null): boolean =>
  b !== null &&
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

/** The next whole hour — the default when a day is picked before a time. */
function nextWholeHour(): Date {
  const date = new Date();
  date.setHours(date.getHours() + 1, 0, 0, 0);
  return date;
}

/**
 * Sunday-first weekday initials for the locale.
 *
 * Built from a known Sunday rather than a hardcoded list so the grid and these
 * headers cannot disagree — `getDay()` is Sunday-indexed, and any list written
 * by hand is one edit away from being offset by one.
 */
function weekdayNames(locale: string): { key: string; label: string }[] {
  // 2024-01-07 was a Sunday, and `getDay()` is Sunday-indexed. Narrow names
  // repeat within a week (S/S, T/T), so the reference date carries the key.
  return Array.from({ length: 7 }, (_, offset) => {
    const day = new Date(2024, 0, 7 + offset);
    return {
      key: day.toISOString(),
      label: day.toLocaleDateString(locale, { weekday: 'narrow' }),
    };
  });
}

interface GridCell {
  key: string;
  /** null for the blanks before day 1. */
  date: Date | null;
}

/** The month's days, padded so day 1 lands under its weekday. */
function monthGrid(month: Date): GridCell[] {
  const year = month.getFullYear();
  const index = month.getMonth();
  const leading = new Date(year, index, 1).getDay();
  // Day 0 of the next month is the last day of this one.
  const total = new Date(year, index + 1, 0).getDate();

  return [
    ...Array.from({ length: leading }, (_, slot) => ({
      key: `${year}-${index}-blank-${slot}`,
      date: null,
    })),
    ...Array.from({ length: total }, (_, day) => {
      const date = new Date(year, index, day + 1);
      return { key: date.toISOString(), date };
    }),
  ];
}
